import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtLargeNumber } from "@/lib/api";
import { useLang, t } from "@/context/LangContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { ArrowLeft, MessageCircle } from "lucide-react";

const RANGES = [
  { key: "1w", label: "1W" },
  { key: "1m", label: "1M" },
  { key: "3m", label: "3M" },
  { key: "1y", label: "1Y" },
  { key: "5y", label: "5Y" },
];

const StockDetail = () => {
  const { symbol } = useParams();
  const { lang } = useLang();
  const [stock, setStock] = useState(null);
  const [valuations, setValuations] = useState(null);
  const [range, setRange] = useState("1m");
  const [history, setHistory] = useState(null);
  const [histSource, setHistSource] = useState(null);

  useEffect(() => {
    api.get(`/stocks/${encodeURIComponent(symbol)}`).then((r) => setStock(r.data));
    api.post(`/calc/all/${encodeURIComponent(symbol)}`).then((r) => setValuations(r.data));
  }, [symbol]);

  useEffect(() => {
    api.get(`/stocks/${encodeURIComponent(symbol)}/history`, { params: { range } }).then((r) => {
      setHistory(r.data.history);
      setHistSource(r.data.source);
    });
  }, [symbol, range]);

  if (!stock) return <div className="max-w-[1400px] mx-auto px-6 py-16 text-white/60">Loading...</div>;

  const cur = stock.currency;
  const fmt = (n) => cur === "IDR" ? n?.toLocaleString("id-ID") : n?.toFixed(2);
  const positive = stock.change_pct >= 0;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="stock-detail-page">
      <Link to="/analysis" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="back-to-list">
        <ArrowLeft size={16} /> {t(lang, "Kembali ke Database", "Back to Database")}
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div>
          <div className="overline text-white/40 mb-2">{stock.exchange} · {stock.sector}</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter">{stock.symbol}</h1>
          <div className="text-white/60 text-lg mt-1">{stock.name}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-4xl md:text-5xl font-bold">
            {cur === "IDR" ? "Rp " : "$"}{fmt(stock.price)}
          </div>
          <div className={`font-mono text-sm mt-1 ${positive ? "text-emerald-400" : "text-rose-400"}`}>
            {positive ? "▲" : "▼"} {Math.abs(stock.change).toFixed(2)} ({Math.abs(stock.change_pct).toFixed(2)}%)
          </div>
          {(stock.price_source === "yahoo" || stock.price_source === "alpha_vantage") && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="live-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              LIVE · {stock.latest_trading_day} · {stock.price_source === "yahoo" ? "Yahoo" : "AV"}
            </div>
          )}
          {stock.price_source === "curated" && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-full bg-white/5 text-white/50 border border-white/10" data-testid="curated-badge">
              CURATED
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="border border-white/10 rounded-2xl p-6 lg:p-8 mb-6" data-testid="stock-chart">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="overline text-white/40">{t(lang, "Grafik Historis", "Historical Chart")}</div>
          <div className="flex items-center gap-3">
            {histSource === "yahoo" && (
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-testid="history-source-live">
                Yahoo · Real
              </span>
            )}
            {histSource === "synthetic" && (
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-white/50 border border-white/10">
                Synthesized
              </span>
            )}
            <div className="flex items-center border border-white/10 rounded-full p-0.5" data-testid="range-switcher">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  data-testid={`range-${r.key}`}
                  className={`px-3 py-1 text-xs font-mono font-medium rounded-full transition-colors ${range === r.key ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="h-64">
          {history ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history} margin={{ left: 0, right: 20, top: 10, bottom: 0 }}>
                <XAxis dataKey="date" stroke="#666" fontSize={11} minTickGap={30} />
                <YAxis stroke="#666" fontSize={11} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "#0d0d0d", border: "1px solid #333", borderRadius: 8, fontFamily: "JetBrains Mono" }}
                  labelStyle={{ color: "#999" }}
                />
                <ReferenceLine y={stock.price} stroke="#666" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="price" stroke={positive ? "#10b981" : "#f43f5e"} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-white/40 text-sm">Loading chart...</div>
          )}
        </div>
      </div>

      {/* Fundamental Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="fundamentals-grid">
        {[
          { label: "Market Cap", value: fmtLargeNumber(stock.market_cap) },
          { label: "P/E Ratio", value: stock.pe > 0 ? stock.pe.toFixed(2) : "N/A" },
          { label: "P/B Ratio", value: stock.pbv?.toFixed(2) },
          { label: "EPS", value: fmt(stock.eps) },
          { label: "Book Value", value: fmt(stock.book_value) },
          { label: t(lang, "Dividend Yield", "Dividend Yield"), value: stock.dividend_yield + "%" },
          { label: t(lang, "Growth Rate", "Growth Rate"), value: stock.growth_rate + "%" },
          { label: "Beta", value: stock.beta?.toFixed(2) },
        ].map((k) => (
          <div key={k.label} className="border border-white/10 rounded-xl p-5 bg-[#0d0d0d]">
            <div className="overline text-white/40 mb-2">{k.label}</div>
            <div className="font-mono text-2xl font-bold">{k.value ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Valuations */}
      {valuations && (
        <div className="border border-white/10 rounded-2xl p-6 lg:p-8" data-testid="valuation-summary">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <div className="overline text-white/40 mb-2">{t(lang, "Konsensus Nilai Wajar", "Consensus Fair Value")}</div>
              <div className="font-mono text-4xl font-bold">{cur === "IDR" ? "Rp " : "$"}{valuations.consensus_fair_value ? fmt(valuations.consensus_fair_value) : "—"}</div>
            </div>
            <div className="text-right">
              <div className="overline text-white/40 mb-2">{t(lang, "Verdict", "Verdict")}</div>
              <div className={`font-display font-bold text-2xl ${valuations.verdict === "Undervalued" ? "text-emerald-400" : valuations.verdict === "Overvalued" ? "text-rose-400" : "text-white/70"}`}>
                {valuations.verdict}
                {valuations.upside_pct !== null && <span className="ml-2 text-sm">({valuations.upside_pct > 0 ? "+" : ""}{valuations.upside_pct}%)</span>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {Object.entries(valuations.results).map(([method, r]) => (
              <div key={method} className="border border-white/10 rounded-lg p-4">
                <div className="overline text-white/40 mb-1">{method.toUpperCase()}</div>
                <div className="font-mono font-bold text-lg">
                  {r.fair_value ? (cur === "IDR" ? "Rp " : "$") + fmt(r.fair_value) : (r.error ? "—" : "—")}
                </div>
                {r.error && <div className="text-xs text-white/40 mt-1">{r.error}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to={`/ai-chat?symbol=${stock.symbol}`} data-testid="ask-ai-btn" className="mt-8 inline-flex items-center gap-2 border border-white/20 px-6 py-3 rounded-full hover:bg-white/5 transition-colors">
        <MessageCircle size={18} /> {t(lang, "Tanya AI Analyst tentang saham ini", "Ask AI Analyst about this stock")}
      </Link>
    </div>
  );
};

export default StockDetail;
